import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  ChefHat,
  UtensilsCrossed,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  Phone,
  Mail,
  Calendar,
  Users,
  Eye,
  Loader2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  useGetAllServiceRequestsQuery,
  useUpdateServiceRequestStatusMutation,
  type ServiceRequest,
  type ServiceRequestStatus,
  type ServiceType,
} from "../../store";
import { TablePagination } from "@/components/admin";
import { toast } from "sonner";

const DEFAULT_PAGE_SIZE = 15;

const STATUS_CONFIG: Record<
  ServiceRequestStatus,
  { label: string; color: string; icon: React.ReactNode }
> = {
  PENDING: {
    label: "Pending",
    color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
    icon: <Clock className="h-3 w-3" />,
  },
  CONTACTED: {
    label: "Contacted",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
    icon: <MessageSquare className="h-3 w-3" />,
  },
  IN_PROGRESS: {
    label: "In Progress",
    color: "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
    icon: <Loader2 className="h-3 w-3" />,
  },
  CONFIRMED: {
    label: "Confirmed",
    color: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
    icon: <CheckCircle className="h-3 w-3" />,
  },
  COMPLETED: {
    label: "Completed",
    color: "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
    icon: <CheckCircle className="h-3 w-3" />,
  },
  CANCELLED: {
    label: "Cancelled",
    color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
    icon: <XCircle className="h-3 w-3" />,
  },
};

export function AdminServiceRequestsScreen() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [filterType, setFilterType] = useState<"ALL" | "CATERING" | "PRIVATE_CHEF">("ALL");
  const [filterStatus, setFilterStatus] = useState<"ALL" | ServiceRequestStatus>("ALL");

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filterType, filterStatus]);

  const { data, isLoading, error } = useGetAllServiceRequestsQuery({
    type: filterType !== "ALL" ? (filterType as ServiceType) : undefined,
    status: filterStatus !== "ALL" ? filterStatus : undefined,
    page,
    limit,
  });
  const [updateStatus, { isLoading: isUpdating }] = useUpdateServiceRequestStatusMutation();
  
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<ServiceRequestStatus>("PENDING");
  const [adminNotes, setAdminNotes] = useState("");

  const requests = data?.data || [];
  const pagination = data?.pagination || {
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };

  // Data is already filtered server-side
  const filteredRequests = requests;

  const handleViewDetails = (request: ServiceRequest) => {
    navigate(`/admin/service-requests/${request.id}`);
  };

  const handleOpenStatusDialog = (request: ServiceRequest) => {
    setSelectedRequest(request);
    setNewStatus(request.status);
    setAdminNotes(request.adminNotes || "");
    setIsStatusDialogOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedRequest) return;

    try {
      await updateStatus({
        id: selectedRequest.id,
        data: { status: newStatus, adminNotes },
      }).unwrap();
      toast.success("Status updated successfully");
      setIsStatusDialogOpen(false);
    } catch (error: any) {
      toast.error(error?.data?.error || "Failed to update status");
    }
  };

  const getServiceIcon = (type: string) => {
    return type === "CATERING" ? (
      <UtensilsCrossed className="h-5 w-5 text-amber-500" />
    ) : (
      <ChefHat className="h-5 w-5 text-orange-500" />
    );
  };

  // Note: These counts are from current page only. For accurate totals, a separate API would be needed.
  const pendingCount = requests.filter((r) => r.status === "PENDING").length;
  const cateringCount = requests.filter((r) => r.type === "CATERING").length;
  const chefCount = requests.filter((r) => r.type === "PRIVATE_CHEF").length;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Failed to load service requests</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Service Requests</h1>
          <p className="text-muted-foreground">
            Manage catering and private chef service inquiries
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Requests</CardDescription>
              <CardTitle className="text-3xl">{pagination.total}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Pending (this page)</CardDescription>
              <CardTitle className="text-3xl text-yellow-600">{pendingCount}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Catering (this page)</CardDescription>
              <CardTitle className="text-3xl text-amber-600">{cateringCount}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Private Chef (this page)</CardDescription>
              <CardTitle className="text-3xl text-orange-600">{chefCount}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <Select value={filterType} onValueChange={(v) => setFilterType(v as any)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Types</SelectItem>
              <SelectItem value="CATERING">Catering</SelectItem>
              <SelectItem value="PRIVATE_CHEF">Private Chef</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as any)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Statuses</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="CONTACTED">Contacted</SelectItem>
              <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
              <SelectItem value="CONFIRMED">Confirmed</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Requests List */}
        <div className="space-y-4">
          {filteredRequests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No service requests found</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {filteredRequests.map((request) => (
                <Card key={request.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="rounded-lg bg-muted p-3">
                          {getServiceIcon(request.type)}
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{request.fullName}</h3>
                            <Badge className={STATUS_CONFIG[request.status].color}>
                              {STATUS_CONFIG[request.status].icon}
                              <span className="ml-1">{STATUS_CONFIG[request.status].label}</span>
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {request.type === "CATERING" ? "Catering Service" : "Private Chef Service"}
                          </p>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {request.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {request.phoneNumber}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {request.numberOfGuests} guests
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {request.eventDate || request.serviceDates || "N/A"}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Submitted {format(new Date(request.createdAt), "PPp")}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(request)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Details
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleOpenStatusDialog(request)}
                        >
                          Update Status
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Pagination */}
              <TablePagination
                pagination={pagination}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
                pageSizeOptions={[10, 15, 20, 30]}
              />
            </>
          )}
        </div>
      </div>

      {/* Status Update Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Request Status</DialogTitle>
            <DialogDescription>
              Update the status for {selectedRequest?.fullName}'s request
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={newStatus} onValueChange={(v) => setNewStatus(v as ServiceRequestStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="CONTACTED">Contacted</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Admin Notes</Label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes about this request..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsStatusDialogOpen(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateStatus} disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Status"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AdminServiceRequestsScreen;
